import axios, { HttpStatusCode } from 'axios';

import config from '@/config';
import { router } from '@/router/router';
import { useToasts } from '@/composables';
import { useAuthStore } from '@/store';

axios.defaults.baseURL = config.apiUrl;
axios.defaults.withCredentials = true;
axios.defaults.headers.common['Content-Type'] = 'application/json';

axios.interceptors.request.use(config => {
  /*
     * If the data being sent in the request is form data, let the browser
     * automatically set the Content-Type header to multipart/form-data,
     * otherwise send it as application/json for all other requests
     */
  if (config.data && config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }

  return config;
});

axios.interceptors.response.use(response => {
  return response;
}, async err => {
  // Handle API call failures due to a problem with the user's internet connection
  if (!err.response) {
    const response = {
      statusCode: null,
      exception: 'An unknown error occurred',
    };

    return Promise.reject(response);
  }

  /*
   * A 401 on a request from an already-authenticated user means their session was ended
   * server-side (e.g. their role was changed). Clear the local auth state and send them to
   * login. A 401 during login itself is left to the login form, hence the isAuthenticated guard.
   */
  const authStore = useAuthStore();

  if (err.response.status === HttpStatusCode.Unauthorized && authStore.isAuthenticated) {
    authStore.user = null;
    await router.push({ name: 'app_auth_login' });
    useToasts().info('Your session has expired. Please log in again.');

    return Promise.reject(err);
  }

  /*
   * Don't redirect in development for failed API requests. This makes it easier for debugging, because if there's a problem,
   * the developer doesn't have to navigate back to where the problem occurred before being able to debug it.
   * Toast the API's message instead for anything production would act on, so a failed request is never silent.
   */
  if (import.meta.env.DEV) {
    const handledByCaller = [
      HttpStatusCode.BadRequest,
      HttpStatusCode.Unauthorized,
      HttpStatusCode.NotFound,
      HttpStatusCode.UnprocessableEntity,
    ];

    if (!handledByCaller.includes(err.response.status)) {
      useToasts().error(`${err.response.status}: ${err.response.data?.exception || 'Request failed'}`);
    }

    return Promise.reject(err);
  }

  const toasts = useToasts();

  switch (err.response.status) {
    // Error response `404`
    case HttpStatusCode.NotFound:
      break;

      // Error response `50x`
    case HttpStatusCode.InternalServerError:
      toasts.error('Server Error. There seems to be an issue with our server. Please try again later.');
      break;

      /*
         * If the server responds with an bad request error, don't do anything, because that is something that the application should handle.
         * If the server responds with an unauthorized error, don't do anything, because that is something that the application will handle.
         * If the server responds with a validation error, don't do anything, because the validation errors will be handled by the component that made the API request.
         * Error response `401` and `422` respectively
         */
    case HttpStatusCode.BadRequest:
    case HttpStatusCode.Unauthorized:
    case HttpStatusCode.UnprocessableEntity:
      break;
      /*
      * A 409 means the resource is in a state that forbids the action, such as a plan locked for
      * review, or a PDF already being generated. The message is kept generic because the same handler covers
      * every 409 the API can raise, and the API does not return the specific reason text in production.
      */
    case HttpStatusCode.Conflict:
      toasts.error('Failed to save plan. This document is locked.');
      break;

      // Error response `404`
    case HttpStatusCode.Forbidden:
      await router.push({ name: 'app_forbidden' });
      break;

    default:
      await router.push({ name: 'app_unknown_error' });
  }

  return Promise.reject(err);
});

const defaultHeaders = {
  'Content-Type': 'application/json',
};

export const HttpClient = {
  async request(method, url, body = null, queryParams = null, headers = null) {
    const config = { method, url, headers, params: queryParams };

    if (body) {
      config.data = body;
    }

    try {
      const response = await axios(config);

      return response.data;
    } catch (err) {
      if (err.response) {
        return err.response.data;
      }

      return { statusCode: null, exception: 'An unknown error occurred' };
    }
  },
  get(url, query, headers) {
    return this.request('GET', url, null, query, headers || defaultHeaders);
  },
  post(url, body, headers) {
    return this.request('POST', url, body, null, headers || defaultHeaders);
  },
  put(url, body, headers) {
    return this.request('PUT', url, body, null, headers || defaultHeaders);
  },
  delete(url, headers) {
    return this.request('DELETE', url, null, null, headers || defaultHeaders);
  },
};
